import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { StagedActionState } from "ai-assistant/staged-actions/statuses/staged-action-state";
import { z } from "zod";

export type StagedActionJSON = z.infer<typeof StagedActionSchema.StagedActionJSONZ>;
export type StagedActionParams = z.infer<typeof StagedActionSchema.StagedActionZ>;

const StagedActionDetailZ = z.object({
  label: z.string(),
  value: z.string(),
});

export default class StagedActionSchema {
  public static readonly StagedActionJSONZ = z.object({
    id: z.uuid(),
    /** The capability's wire name, so a client can specialise a view for one later. */
    capability: z.string(),
    summary: z.string(),
    details: z.array(StagedActionDetailZ),
    expiresAt: z.iso.datetime({ offset: true }),
    state: z.enum(StagedActionState),
  });

  public static readonly StagedActionZ = z.object({
    id: z.instanceof(Guid),
    capability: z.string(),
    summary: z.string(),
    details: z.array(StagedActionDetailZ),
    expiresAt: z.custom<AHDate>(),
    state: z.enum(StagedActionState),
  });

  public static validate(values: unknown): StagedActionParams {
    return parseOrThrow(StagedActionSchema.StagedActionZ, values, {
      logError: true,
      schemaName: "StagedActionSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): StagedActionJSON {
    return parseOrThrow(StagedActionSchema.StagedActionJSONZ, json, {
      logError: true,
      schemaName: "StagedActionSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is StagedActionParams {
    try {
      StagedActionSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is StagedActionJSON {
    try {
      StagedActionSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: StagedActionJSON): StagedActionParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(StagedActionSchema.StagedActionJSONZ, json, {
      logError: true,
      schemaName: "StagedActionSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return StagedActionSchema.codec.decode(parsed);
  }

  public static serialize(action: StagedActionParams): StagedActionJSON {
    if (isNullOrUndefined(action)) return action;
    const parsed = StagedActionSchema.validate(action);
    if (isNullOrUndefined(parsed)) return parsed;
    return StagedActionSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    StagedActionSchema.StagedActionJSONZ,
    StagedActionSchema.StagedActionZ,
    {
      decode: (data) => ({
        id: new Guid(data.id),
        capability: data.capability,
        summary: data.summary,
        details: data.details,
        expiresAt: new AHDate(data.expiresAt),
        state: data.state as StagedActionState,
      }),
      encode: (action) => ({
        id: action.id.value,
        capability: action.capability,
        summary: action.summary,
        details: action.details,
        expiresAt: action.expiresAt.toISOString(),
        state: action.state,
      }),
    }
  );
}
